import React, { useMemo } from 'react';
import { Stack, Text, Group, Badge, Divider, Paper, Tooltip } from '@mantine/core';
import { Clock, FileText, List, Target, Calendar, Users, Star, TrendingUp, AlertCircle, Zap } from 'lucide-react';
import { useDashboardData } from '../../../../hooks/dashboard';

interface HighlightedTask {
  task: any;
  reason: string;
  icon: React.ComponentType<any>;
  color: string;
  description: string;
}

interface InsightsWidgetProps {
  onEditTodo?: (todoId: number) => void;
}

export const InsightsWidget: React.FC<InsightsWidgetProps> = ({ onEditTodo }) => {
  const dashboardData = useDashboardData();

  const highlightedTask = useMemo((): HighlightedTask | null => {
    if (dashboardData.todos.length === 0) return null;

    // Find task with most comments (indicating high activity/collaboration)
    const taskWithMostComments = dashboardData.todos
      .filter(t => t.comments && t.comments.length > 0)
      .sort((a, b) => (b.comments?.length || 0) - (a.comments?.length || 0))[0];

    if (taskWithMostComments && taskWithMostComments.comments && taskWithMostComments.comments.length >= 3) {
      return {
        task: taskWithMostComments,
        reason: 'Most Discussed',
        icon: TrendingUp,
        color: 'blue',
        description: `${taskWithMostComments.comments.length} comments`
      };
    }

    // Find overdue task that's been around the longest
    const oldestOverdueTask = dashboardData.todos
      .filter(t => t.dueDate && !t.isCompleted && new Date(t.dueDate) < new Date())
      .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())[0];

    if (oldestOverdueTask) {
      const daysOverdue = Math.floor((new Date().getTime() - new Date(oldestOverdueTask.dueDate!).getTime()) / (1000 * 60 * 60 * 24));
      return {
        task: oldestOverdueTask,
        reason: 'Needs Attention',
        icon: AlertCircle,
        color: 'red',
        description: `${daysOverdue} days overdue`
      };
    }

    // Find task with highest priority
    const highPriorityTask = dashboardData.todos
      .filter(t => !t.isCompleted && t.priority === 'High')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

    if (highPriorityTask) {
      return {
        task: highPriorityTask,
        reason: 'High Priority',
        icon: Star,
        color: 'orange',
        description: 'Marked as urgent'
      };
    }

    // Find recently completed task (last 24 hours)
    const recentlyCompleted = dashboardData.todos
      .filter(t => t.isCompleted && t.completedAt)
      .filter(t => {
        const completedTime = new Date(t.completedAt!).getTime();
        const dayAgo = new Date().getTime() - (24 * 60 * 60 * 1000);
        return completedTime > dayAgo;
      })
      .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime())[0];

    if (recentlyCompleted) {
      const hoursAgo = Math.floor((new Date().getTime() - new Date(recentlyCompleted.completedAt!).getTime()) / (1000 * 60 * 60));
      return {
        task: recentlyCompleted,
        reason: 'Recently Completed',
        icon: Zap,
        color: 'green',
        description: `${hoursAgo === 0 ? 'Just now' : `${hoursAgo}h ago`}`
      };
    }

    // Find task with due date coming up soon (next 3 days)
    const upcomingTask = dashboardData.todos
      .filter(t => !t.isCompleted && t.dueDate)
      .filter(t => {
        const due = new Date(t.dueDate!);
        const now = new Date();
        const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
        return due >= now && due <= threeDaysFromNow;
      })
      .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())[0];

    if (upcomingTask) {
      const daysUntilDue = Math.ceil((new Date(upcomingTask.dueDate!).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return {
        task: upcomingTask,
        reason: 'Due Soon',
        icon: Clock,
        color: 'yellow',
        description: `Due in ${daysUntilDue} day${daysUntilDue === 1 ? '' : 's'}`
      };
    }

    return null;
  }, [dashboardData.todos]);

  const insights = useMemo(() => {
    const insights: Array<{ icon: React.ComponentType<any>; label: string; value: string; color: string }> = [];

    // Note insights
    if (dashboardData.notes.length > 0) {
      insights.push({
        icon: FileText,
        label: 'Notes created',
        value: `${dashboardData.notes.length}`,
        color: 'purple'
      });
    }

    // Todo list insights
    if (dashboardData.todoLists.length > 0) {
      insights.push({
        icon: List,
        label: 'Active lists',
        value: `${dashboardData.todoLists.length}`,
        color: 'blue'
      });
    }

    // Task insights
    if (dashboardData.todos.length > 0) {
      const completedTasks = dashboardData.todos.filter(t => t.isCompleted).length;

      insights.push({
        icon: Target,
        label: 'Tasks created',
        value: `${dashboardData.todos.length}`,
        color: 'green'
      });

      if (completedTasks > 0) {
        insights.push({
          icon: Target,
          label: 'Tasks completed',
          value: `${completedTasks}`,
          color: 'teal'
        });
      }
    }

    // Task with due dates
    const tasksWithDueDates = dashboardData.todos.filter(t => t.dueDate).length;
    if (tasksWithDueDates > 0) {
      insights.push({
        icon: Calendar,
        label: 'Tasks with deadlines',
        value: `${tasksWithDueDates}`,
        color: 'orange'
      });
    }

    // Shared lists
    const sharedLists = dashboardData.todoLists.filter(list =>
      list.sharedWith && list.sharedWith.length > 0
    ).length;
    if (sharedLists > 0) {
      insights.push({
        icon: Users,
        label: 'Shared lists',
        value: `${sharedLists}`,
        color: 'pink'
      });
    }

    // Average tasks per list
    if (dashboardData.todoLists.length > 0 && dashboardData.todos.length > 0) {
      const avgTasksPerList = Math.round(dashboardData.todos.length / dashboardData.todoLists.length);
      if (avgTasksPerList > 1) {
        insights.push({
          icon: List,
          label: 'Avg tasks per list',
          value: `${avgTasksPerList}`,
          color: 'cyan'
        });
      }
    }

    // Most productive day insight
    if (dashboardData.recentlyCompletedTasks.length >= 3) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayStats = new Map<number, number>();

      dashboardData.recentlyCompletedTasks.forEach(task => {
        if (task.completedAt) {
          const day = new Date(task.completedAt).getDay();
          dayStats.set(day, (dayStats.get(day) || 0) + 1);
        }
      });

      if (dayStats.size > 0) {
        const mostProductiveDay = Array.from(dayStats.entries())
          .sort((a, b) => b[1] - a[1])[0];

        if (mostProductiveDay[1] >= 2) {
          insights.push({
            icon: Clock,
            label: 'Most productive day',
            value: dayNames[mostProductiveDay[0]],
            color: 'violet'
          });
        }
      }
    }

    return insights;
  }, [dashboardData]);

  if (insights.length === 0) {
    return null;
  }

  return (
    <Stack gap="md">
      {/* Highlighted Task Section */}
      {highlightedTask && (
        <Paper
          p="sm"
          withBorder
          style={{
            backgroundColor: `var(--mantine-color-${highlightedTask.color}-0)`,
            borderColor: `var(--mantine-color-${highlightedTask.color}-3)`,
            borderWidth: '1px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => onEditTodo?.(highlightedTask.task.id)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                <highlightedTask.icon
                  size={16}
                  style={{
                    color: `var(--mantine-color-${highlightedTask.color}-6)`,
                    flexShrink: 0,
                    marginTop: '1px'
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="xs" fw={600} c={highlightedTask.color} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {highlightedTask.reason}
                  </Text>
                </div>
              </Group>
              <Badge size="xs" variant="filled" color={highlightedTask.color}>
                {highlightedTask.description}
              </Badge>
            </Group>
            <Tooltip label={highlightedTask.task.title} multiline withinPortal>
              <Text
                size="sm"
                fw={500}
                style={{
                  color: 'var(--app-text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'help'
                }}
              >
                {highlightedTask.task.title}
              </Text>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      {/* Regular Insights Section */}
      <Stack gap="xs">
        <Text size="xs" c="dimmed" fw={500} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Activity Overview
        </Text>

        {insights.map((insight, index) => {
          const Icon = insight.icon;
          return (
            <Group key={index} justify="space-between" align="center">
              <Group gap="xs" style={{ flex: 1 }}>
                <Icon
                  size={14}
                  style={{
                    color: `var(--mantine-color-${insight.color}-6)`,
                    flexShrink: 0
                  }}
                />
                <Text
                  size="sm"
                  style={{
                    color: 'var(--app-text-primary)',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {insight.label}
                </Text>
              </Group>
              <Badge
                size="sm"
                variant="light"
                color={insight.color}
                style={{ flexShrink: 0 }}
              >
                {insight.value}
              </Badge>
            </Group>
          );
        })}
      </Stack>

      {(insights.length > 4 || highlightedTask) && (
        <>
          <Divider size="xs" color="var(--app-border)" />
          <Text size="xs" c="dimmed" ta="center">
            ✨ Keep building your productivity habits!
          </Text>
        </>
      )}
    </Stack>
  );
};